import {
  HolidaysAction,
  HOLIDAYS_OPEN_MODAL,
  HOLIDAYS_CLOSE_MODAL,
  HOLIDAYS_SET_FROM_API,
  HOLIDAYS_SET_CALENDAR_MONTH,
  HOLIDAYS_START_DRAG,
  HOLIDAYS_HOVER_DRAG,
  HOLIDAYS_FINALIZE_DRAG,
  HOLIDAYS_UPDATE_NAME,
  HOLIDAYS_UPDATE_REPEAT,
  HOLIDAYS_REQUEST_DELETE,
  HOLIDAYS_CANCEL_DELETE,
  HOLIDAYS_DISCARD_DRAFT,
} from 'actions/holidaysActions';
import { HolidaysState, HolidayBlock } from './Holidays.types';
import { fromApiDateTime } from './utils/holidayDateUtils';

export type { HolidaysAction };

const today = new Date();

const draftForSites = (bySite: HolidaysState['bySite'], sites: string[]) =>
  sites
    .flatMap(site => bySite[site] ?? [])
    .sort((a, b) => a.start.localeCompare(b.start));

const overlaps = (block: HolidayBlock, lo: string, hi: string) => block.end >= lo && block.start <= hi;

export const INITIAL_HOLIDAYS_STATE: HolidaysState = {
  bySite: {},
  isModalOpen: false,
  activeSites: [],
  draft: [],
  calendarYear: today.getFullYear(),
  calendarMonth: today.getMonth(),
  dragAnchor: null,
  dragHover: null,
  pendingDeleteId: null,
};

export const holidaysReducer = (
  state: HolidaysState = INITIAL_HOLIDAYS_STATE,
  action: HolidaysAction
): HolidaysState => {
  switch (action.type) {
    case HOLIDAYS_OPEN_MODAL: {
      const now = new Date();
      return {
        ...state,
        isModalOpen: true,
        activeSites: action.payload.sites,
        draft: draftForSites(state.bySite, action.payload.sites),
        calendarYear: now.getFullYear(),
        calendarMonth: now.getMonth(),
        dragAnchor: null,
        dragHover: null,
      };
    }

    case HOLIDAYS_CLOSE_MODAL:
      return {
        ...state,
        isModalOpen: false,
        activeSites: [],
        draft: [],
        dragAnchor: null,
        dragHover: null,
        pendingDeleteId: null,
      };

    case HOLIDAYS_SET_FROM_API: {
      const { response } = action.payload;
      const bySite = { ...state.bySite };
      response.siteNames.forEach(site => {
        bySite[site] = [];
      });
      response.holidays.forEach(h => {
        bySite[h.siteName] = [...(bySite[h.siteName] ?? []), {
          id: String(h.id),
          start: fromApiDateTime(h.startDate),
          end: fromApiDateTime(h.endDate),
          name: h.name,
          repeat: h.repeatYearly,
          siteNames: [h.siteName],
        }];
      });
      // Only refresh the working draft for responses touching the sites the modal is open
      // for — a response for a background site (e.g. still in flight after the modal was
      // reopened for other sites) shouldn't touch it.
      const touchesActiveSites = response.siteNames.some(site => state.activeSites.includes(site));
      return {
        ...state,
        bySite,
        draft:
          state.isModalOpen && touchesActiveSites
            ? draftForSites(bySite, state.activeSites)
            : state.draft,
      };
    }

    case HOLIDAYS_SET_CALENDAR_MONTH:
      return { ...state, calendarYear: action.payload.year, calendarMonth: action.payload.month };

    case HOLIDAYS_START_DRAG:
      return { ...state, dragAnchor: action.payload.dateKey, dragHover: action.payload.dateKey };

    case HOLIDAYS_HOVER_DRAG:
      if (!state.dragAnchor) return state;
      return { ...state, dragHover: action.payload.dateKey };

    case HOLIDAYS_FINALIZE_DRAG: {
      const { dragAnchor, dragHover } = state;
      if (!dragAnchor) return state;
      const anchor = dragAnchor;
      const hover = dragHover ?? dragAnchor;
      const lo = anchor <= hover ? anchor : hover;
      const hi = anchor <= hover ? hover : anchor;

      const savedIds = new Set(draftForSites(state.bySite, state.activeSites).map(b => b.id));
      const unsaved = state.draft.filter(b => !savedIds.has(b.id));
      // A new block is created only for the sites that don't already have a saved day off in its range.
      const targetSites = state.activeSites.filter(
        site => !(state.bySite[site] ?? []).some(b => overlaps(b, lo, hi))
      );
      let draft: HolidayBlock[];
      const unsavedAtRange = unsaved.find(b => overlaps(b, lo, hi));
      if (unsavedAtRange) {
        // Clicking an unsaved block again toggles it back off; dragging over one is ignored.
        draft = lo === hi ? state.draft.filter(b => b.id !== unsavedAtRange.id) : state.draft;
      } else if (targetSites.length === 0) {
        // Already saved for every site — can't be toggled off by clicking, only via the trash icon.
        draft = state.draft;
      } else {
        draft = [
          ...state.draft,
          { id: `holiday-${Date.now()}`, start: lo, end: hi, name: '', repeat: false, siteNames: targetSites },
        ];
      }

      return {
        ...state,
        draft: draft.sort((a, b) => a.start.localeCompare(b.start)),
        dragAnchor: null,
        dragHover: null,
      };
    }

    case HOLIDAYS_UPDATE_NAME:
      return {
        ...state,
        draft: state.draft.map(b =>
          b.id === action.payload.id ? { ...b, name: action.payload.name } : b
        ),
      };

    case HOLIDAYS_UPDATE_REPEAT:
      return {
        ...state,
        draft: state.draft.map(b =>
          b.id === action.payload.id ? { ...b, repeat: action.payload.repeat } : b
        ),
      };

    case HOLIDAYS_REQUEST_DELETE:
      return { ...state, pendingDeleteId: action.payload.id };

    case HOLIDAYS_CANCEL_DELETE:
      return { ...state, pendingDeleteId: null };

    case HOLIDAYS_DISCARD_DRAFT:
      return { ...state, draft: state.draft.filter(b => b.id !== action.payload.id) };

    default:
      return state;
  }
};
